import React, { useState, useEffect } from 'react';
import { Button, Input, message, Popconfirm, Space, Table, TableColumnsType, Tag } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import {
  getSkillRegistryList,
  getSkillVersionList,
  deleteSkillRegistry,
  deleteSkillVersion,
  switchSkillVersion,
  SkillRegistryVo,
  SkillVersionVo,
} from '@/api/skill';
import {
  getSkillToolBindings,
  getAvailableTools,
  SkillToolBindingVo,
} from '@/api/skillToolBinding';
import { getMcpServerList, McpServerConfigVo } from '@/api/mcpServer';
import { useUpdate } from '@/common/hook';
import EditSkillRegistry from './editRegistryModal';
import EditSkillVersion from './editVersionModal';
import SkillExpandRow from './skillExpandRow';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

const registryColumns: TableColumnsType<SkillRegistryVo> = [
  { title: 'Skill 名称', dataIndex: 'name', key: 'name', width: 150 },
  { title: '描述', dataIndex: 'description', key: 'description', width: 200, ellipsis: true },
  {
    title: '当前版本',
    dataIndex: 'currentVersion',
    key: 'currentVersion',
    width: 100,
    render: (version: string) => version ? <Tag color="blue">{version}</Tag> : <Tag>无版本</Tag>,
  },
  {
    title: '创建人',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: 100,
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
  {
    title: '更新时间',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 160,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const versionColumns: TableColumnsType<SkillVersionVo> = [
  { title: '版本号', dataIndex: 'version', key: 'version', width: 100 },
  {
    title: '变更说明',
    dataIndex: 'changeLog',
    key: 'changeLog',
    ellipsis: true,
  },
  {
    title: '创建人',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: 100,
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
  {
    title: '更新人',
    dataIndex: 'updatedBy',
    key: 'updatedBy',
    width: 100,
  },
  {
    title: '更新时间',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const SkillRegistryList = () => {
  const [editData, setEditData] = useState<SkillRegistryVo>();
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const [update, updateFn] = useUpdate();

  // 版本相关状态
  const [versionData, setVersionData] = useState<SkillVersionVo>();
  const [versionVisible, setVersionVisible] = useState<boolean>(false);
  const [currentSkillId, setCurrentSkillId] = useState<number>(0);

  // 展开行相关状态
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [versionMap, setVersionMap] = useState<Map<number, SkillVersionVo[]>>(new Map());
  const [toolBindingMap, setToolBindingMap] = useState<Map<number, SkillToolBindingVo[]>>(new Map());

  // MCP Server 列表
  const { data: mcpServerList } = useRequest(() => getMcpServerList());

  // 可用工具列表
  const { data: availableTools } = useRequest(() => getAvailableTools());

  const { data: registryList, loading } = useRequest(() => getSkillRegistryList({ keyword }), {
    refreshDeps: [update, keyword],
  });

  // 当展开行变化时，加载对应数据
  useEffect(() => {
    expandedKeys.forEach(key => {
      const skillId = Number(key);
      if (!versionMap.has(skillId)) {
        getSkillVersionList(skillId).then(list => {
          setVersionMap(prev => new Map(prev).set(skillId, list || []));
        });
      }
      if (!toolBindingMap.has(skillId)) {
        getSkillToolBindings(skillId).then(list => {
          setToolBindingMap(prev => new Map(prev).set(skillId, list || []));
        });
      }
    });
  }, [expandedKeys]);

  const onDeleteRegistry = (id: number) => {
    deleteSkillRegistry(id).then(() => {
      message.success('删除成功');
      updateFn();
      setVersionMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
    });
  };

  const onDeleteVersion = (skillId: number, versionId: number) => {
    deleteSkillVersion(versionId).then(() => {
      message.success('删除成功');
      getSkillVersionList(skillId).then(list => {
        setVersionMap(prev => new Map(prev).set(skillId, list || []));
      });
    });
  };

  const handleSwitchVersion = (skillId: number, versionId: number) => {
    switchSkillVersion({ skillId, versionId }).then(() => {
      message.success('版本切换成功');
      updateFn();
    });
  };

  const handleExpand = (expanded: boolean, record: SkillRegistryVo) => {
    if (expanded) {
      setExpandedKeys([...expandedKeys, record.id]);
    } else {
      setExpandedKeys(expandedKeys.filter(key => key !== record.id));
    }
  };

  const buildColumns = [
    ...registryColumns,
    {
      title: '操作',
      key: 'operation',
      width: 120,
      render: (_: any, record: SkillRegistryVo) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setEditData(record);
              setEditVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm title="确定删除该 Skill 及其所有版本？" onConfirm={() => onDeleteRegistry(record.id)}>
            <Button type="link" size="small" style={{ padding: 0 }} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (skill: SkillRegistryVo) => {
    const versionList = versionMap.get(skill.id) || [];
    const toolBindings = toolBindingMap.get(skill.id) || [];

    return (
      <SkillExpandRow
        skill={skill}
        versionList={versionList}
        toolBindings={toolBindings}
        availableTools={availableTools}
        mcpServerList={mcpServerList}
        onAddVersion={(skillId) => {
          setCurrentSkillId(skillId);
          setVersionData(undefined);
          setVersionVisible(true);
        }}
        onEditVersion={(skillId, version) => {
          setCurrentSkillId(skillId);
          setVersionData(version);
          setVersionVisible(true);
        }}
        onSwitchVersion={(skillId, versionId) => handleSwitchVersion(skillId, versionId)}
        onDeleteVersion={(skillId, versionId) => onDeleteVersion(skillId, versionId)}
        onToolBindingsChange={(skillId, bindings) => {
          setToolBindingMap(prev => new Map(prev).set(skillId, bindings));
        }}
      />
    );
  };

  return (
    <>
      <div className="flex gap-3 mb-3">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditVisible(true)}>
          新增 Skill
        </Button>
        <Input
          placeholder="搜索 Skill 名称"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>
      <Table<SkillRegistryVo>
        columns={buildColumns}
        dataSource={registryList ?? []}
        loading={loading}
        rowKey="id"
        expandable={{
          expandedRowRender,
          expandedRowKeys: expandedKeys,
          onExpand: handleExpand,
          expandIcon: ({ expanded, onExpand, record }) => (
            <span
              onClick={e => {
                e.stopPropagation();
                onExpand(record, e);
              }}
              style={{ cursor: 'pointer', padding: '4px 8px' }}
            >
              {expanded ? '▼' : '▶'}
            </span>
          ),
        }}
        onRow={(record) => ({
          onClick: () => handleExpand(!expandedKeys.includes(record.id), record),
          style: { cursor: 'pointer' },
        })}
      />
      {editVisible && (
        <EditSkillRegistry
          data={editData}
          isEdit={!!editData}
          updateFn={updateFn}
          onCancel={() => {
            setEditVisible(false);
            setEditData(undefined);
          }}
        />
      )}
      {versionVisible && (
        <EditSkillVersion
          skillId={currentSkillId}
          data={versionData}
          isEdit={!!versionData}
          isFirstVersion={!versionMap.has(currentSkillId) || (versionMap.get(currentSkillId)?.length === 0)}
          updateFn={() => {
            updateFn();
            getSkillVersionList(currentSkillId).then(list => {
              setVersionMap(prev => new Map(prev).set(currentSkillId, list || []));
            });
          }}
          onCancel={() => {
            setVersionVisible(false);
            setVersionData(undefined);
          }}
        />
      )}
    </>
  );
};

export default SkillRegistryList;